import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ArrowDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { DateInput } from '@/components/ui/date-input'
import { Label } from '@/components/ui/label'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { useAccounts } from '@/hooks/useAccounts'
import { accountIcon } from '@/lib/accountIcon'
import { accountComboOptions } from '@/lib/accountOptions'
import { useSaveTransfer, useUpdateTransfer } from '@/hooks/useTransactions'
import { useT } from '@/i18n/I18nProvider'

function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function TransferDialog({
  open,
  onOpenChange,
  defaultFromId,
  defaultToId,
  defaultAmount,
  defaultToAmount,
  defaultDate,
  defaultDescription,
  editGroupId,
  title,
  onDone,
  payMode = false,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  defaultFromId?: string
  defaultToId?: string
  defaultAmount?: string
  defaultToAmount?: string
  defaultDate?: string
  defaultDescription?: string
  /** Quando presente, edita a transferência desse grupo (recria o par no mesmo id). */
  editGroupId?: string
  title?: string
  /** Chamado após a transferência ser salva. A fatura do cartão usa isso para
   *  quitar as compras do mês (o pagamento da fatura as liquida). */
  onDone?: () => void | Promise<void>
  /** Modo PAGAMENTO de fatura: o destino é o cartão (fixo em defaultToId) e a UI
   *  fala "pagar" em vez de "transferir". A mecânica continua sendo um par de
   *  lançamentos (conta → cartão) — só muda a linguagem. */
  payMode?: boolean
}) {
  const { t } = useT()
  const { data: accounts } = useAccounts()
  const save = useSaveTransfer()
  const update = useUpdateTransfer()
  const isEdit = Boolean(editGroupId)
  const busy = save.isPending || update.isPending

  const [fromId, setFromId] = useState('')
  const [toId, setToId] = useState('')
  const [amountFrom, setAmountFrom] = useState('')
  const [amountTo, setAmountTo] = useState('')
  const [date, setDate] = useState(todayISO())
  const [description, setDescription] = useState('')

  const fromAcc = useMemo(() => accounts?.find((a) => a.id === fromId), [accounts, fromId])
  const toAcc = useMemo(() => accounts?.find((a) => a.id === toId), [accounts, toId])
  const crossCurrency = Boolean(fromAcc && toAcc && fromAcc.currency !== toAcc.currency)

  // Opções com busca (digitar filtra). Cada lado esconde a conta já escolhida no
  // outro, evitando origem = destino.
  const accountOptions: ComboOption[] = useMemo(() => accountComboOptions(accounts), [accounts])

  // reseta ao abrir
  useEffect(() => {
    if (open) {
      setFromId(defaultFromId ?? '')
      setToId(defaultToId ?? '')
      setAmountFrom(defaultAmount ?? '')
      setAmountTo(defaultToAmount ?? '')
      setDate(defaultDate ?? todayISO())
      setDescription(defaultDescription ?? (payMode ? t('dlg.tr.payDefaultDescription') : ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultFromId, defaultToId, defaultAmount, defaultToAmount, defaultDate, defaultDescription, payMode])

  async function submit() {
    const from = Number(amountFrom)
    if (!fromId || !toId)
      return toast.error(payMode ? t('dlg.tr.errPayAccount') : t('dlg.tr.errAccounts'))
    if (fromId === toId) return toast.error(t('dlg.tr.errSame'))
    if (!(from > 0)) return toast.error(t('dlg.tr.errAmount'))
    const to = crossCurrency ? Number(amountTo) : from
    if (crossCurrency && !(to > 0)) return toast.error(t('dlg.tr.errReceived'))
    const payload = {
      fromAccountId: fromId,
      toAccountId: toId,
      fromAmount: from,
      fromCurrency: fromAcc!.currency,
      toAmount: to,
      toCurrency: toAcc!.currency,
      date,
      description: description.trim() || null,
    }
    try {
      if (isEdit) {
        await update.mutateAsync({ ...payload, groupId: editGroupId! })
        toast.success(t('dlg.tr.updated'))
      } else {
        await save.mutateAsync(payload)
        toast.success(payMode ? t('dlg.tr.paid') : t('dlg.tr.done'))
      }
      await onDone?.()
      onOpenChange(false)
    } catch (e) {
      toast.error(t('dlg.tr.error'), { description: (e as Error).message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title ?? t('dlg.tr.title')}</DialogTitle>
          <DialogDescription>
            {payMode ? t('dlg.tr.descPay') : t('dlg.tr.descTransfer')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{payMode ? t('dlg.tr.payWith') : t('dlg.tr.from')}</Label>
            <Combobox
              options={accountOptions.filter((o) => o.value !== toId)}
              value={fromId}
              onChange={setFromId}
              placeholder={payMode ? t('dlg.tr.payingAccount') : t('dlg.tr.sourceAccount')}
              searchPlaceholder={t('dlg.searchAccount')}
              aria-label={payMode ? t('dlg.tr.payingAccount') : t('dlg.tr.sourceAccount')}
            />
          </div>

          <div className="flex justify-center">
            <ArrowDown className="text-muted-foreground size-4" />
          </div>

          {payMode ? (
            <div className="grid gap-2">
              <Label>{t('dlg.tx.statement')}</Label>
              <div className="border-input bg-muted/40 text-muted-foreground flex h-9 items-center rounded-md border px-3 text-sm">
                {toAcc ? `${accountIcon(toAcc)} ${toAcc.name}` : t('dlg.tr.card')}
              </div>
            </div>
          ) : (
            <div className="grid gap-2">
              <Label>{t('dlg.tr.to')}</Label>
              <Combobox
                options={accountOptions.filter((o) => o.value !== fromId)}
                value={toId}
                onChange={setToId}
                placeholder={t('dlg.tr.destAccount')}
                searchPlaceholder={t('dlg.searchAccount')}
                aria-label={t('dlg.tr.destAccount')}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{t('dlg.amount')} {fromAcc ? `(${fromAcc.currency})` : ''}</Label>
              <MoneyInput
                value={amountFrom ? Number(amountFrom) : 0}
                onChange={(n) => setAmountFrom(n ? String(n) : '')}
              />
            </div>
            {crossCurrency && (
              <div className="grid gap-2">
                <Label>{t('dlg.tr.received')} ({toAcc?.currency})</Label>
                <MoneyInput
                  value={amountTo ? Number(amountTo) : 0}
                  onChange={(n) => setAmountTo(n ? String(n) : '')}
                />
              </div>
            )}
          </div>
          {crossCurrency && (
            <p className="text-muted-foreground text-xs">
              {t('dlg.tr.crossNote')}
            </p>
          )}

          <div className="grid gap-2">
            <Label>{t('dlg.date')}</Label>
            <DateInput value={date} onChange={setDate} />
          </div>

          <div className="grid gap-2">
            <Label>{t('dlg.description')} <span className="text-muted-foreground font-normal">{t('dlg.tr.descOptional')}</span></Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('dlg.tr.descPlaceholder')} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={submit} disabled={busy}>
            {busy
              ? t('common.saving')
              : isEdit
                ? t('common.save')
                : payMode
                  ? t('dlg.tr.pay')
                  : t('dlg.tr.transfer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
